import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Send, Loader2, Sparkles, BookOpen, Heart, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { TopicSelector } from "@/components/TopicSelector";
import { ChatMessage } from "@/components/ChatMessage";
import type { Topic, Message, TopicCategory } from "@/types";

/**
 * Chat page for AI-powered conversations
 * Supports streaming responses and topic management
 *
 * @returns Chat interface with message history and input
 */
export function Chat() {
  const { topicId } = useParams<{ topicId?: string }>();
  const { getToken } = useAuth();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [currentTopic, setCurrentTopic] = useState<Topic | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Category options for new topics
  const categories: { value: TopicCategory; label: string; icon: typeof BookOpen }[] = [
    { value: "study", label: "Study", icon: BookOpen },
    { value: "life", label: "Life", icon: Heart },
    { value: "other", label: "Other", icon: HelpCircle },
  ];

  // Fetch topics and messages
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        if (!token) return;

        const topicsData = await api.getTopics(token);
        setTopics(topicsData);

        if (topicId) {
          const topic = topicsData.find((t) => t.id === topicId);
          if (topic) {
            setCurrentTopic(topic);
            const messagesData = await api.getMessages(token, topicId);
            setMessages(messagesData);
          }
        }
      } catch (error) {
        console.error("Failed to fetch chat data:", error);
      }
    };

    fetchData();
  }, [getToken, topicId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /**
   * Handle sending a message to the AI
   * Uses streaming for real-time response display
   */
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setIsLoading(true);

    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");

      // Add user message to UI
      const userMsg: Message = {
        id: crypto.randomUUID(),
        topicId: currentTopic?.id || "",
        role: "user",
        content: userMessage,
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);

      // Create topic if needed
      let activeTopicId = currentTopic?.id;
      if (!activeTopicId) {
        const newTopic = await api.createTopic(token, {
          name: userMessage.slice(0, 50),
          category: "study",
          description: "",
        });
        setCurrentTopic(newTopic);
        setTopics((prev) => [newTopic, ...prev]);
        activeTopicId = newTopic.id;
      }

      // Prepare placeholder for assistant response
      const assistantMsg: Message = {
        id: crypto.randomUUID(),
        topicId: activeTopicId,
        role: "assistant",
        content: "",
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      // Stream response
      await api.sendMessage(
        token,
        activeTopicId,
        userMessage,
        (chunk) => {
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMsg = newMessages[newMessages.length - 1];
            if (lastMsg.role === "assistant") {
              lastMsg.content += chunk;
            }
            return newMessages;
          });
        }
      );
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle topic selection from sidebar
   */
  const handleTopicSelect = async (topic: Topic) => {
    setCurrentTopic(topic);
    try {
      const token = await getToken();
      if (token) {
        const messagesData = await api.getMessages(token, topic.id);
        setMessages(messagesData);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  return (
    <div className="h-[calc(100vh-6rem)] lg:h-[calc(100vh-4rem)] flex gap-6">
      {/* Topic sidebar */}
      <TopicSelector
        topics={topics}
        currentTopic={currentTopic}
        onSelect={handleTopicSelect}
        onNewTopic={() => {
          setCurrentTopic(null);
          setMessages([]);
        }}
      />

      {/* Chat area */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl border shadow-sm overflow-hidden">
        {/* Chat header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary-50 to-transparent">
          <h2 className="font-display font-semibold text-primary-950">
            {currentTopic?.name || "New Conversation"}
          </h2>
          {currentTopic && (
            <span
              className={`inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded-full ${
                currentTopic.category === "study"
                  ? "bg-study-light text-study-dark"
                  : currentTopic.category === "life"
                  ? "bg-life-light text-life-dark"
                  : "bg-other-light text-other-dark"
              }`}
            >
              {categories.find((c) => c.value === currentTopic.category)?.label}
            </span>
          )}
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="p-4 bg-primary-100 rounded-full mb-4">
                <Sparkles className="text-primary-600" size={32} />
              </div>
              <h3 className="text-xl font-display font-semibold text-primary-950">
                Start a conversation
              </h3>
              <p className="text-slate-500 mt-2 max-w-md">
                Ask me anything about your studies, share your thoughts, or discuss
                any challenges you're facing. I'm here to help!
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="p-4 border-t bg-slate-50/50">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="px-5 py-3 bg-primary-600 text-white rounded-xl font-medium shadow-lg shadow-primary-600/25 hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

